import axios from 'axios';
import echoInstance from "./echo";
import { attachLocaleHeader, t } from "../i18n";

const SESSION_EXPIRED_REASONS = [
    'session_expired',
    'fresh_session_required',
    'editor_idle_timeout',
];

let sessionExpiredEventDispatched = false;

export const client = axios.create({
    baseURL: import.meta.env.VITE_APP_URL,
    withCredentials: true, // required to handle the CSRF token
    headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
});

// Eigene Instanz, eigener Interceptor: Die Sprache der globalen Instanz gilt hier nicht.
attachLocaleHeader(client);

/**
 * Detect whether the failed request is caused by an expired session.
 *
 * Normal expected backend responses:
 * - 419 session_expired
 * - 419 fresh_session_required
 * - 419 editor_idle_timeout
 * - 401 unauthenticated
 *
 * The ERR_NETWORK fallback catches cases like redirect loops during expired web sessions.
 */
export function isSessionExpiredError(error) {
    const status = error.response?.status;
    const reason = error.response?.data?.reason;

    if (status === 419 || status === 401) {
        return true;
    }

    if (SESSION_EXPIRED_REASONS.includes(reason)) {
        return true;
    }

    /**
     * Browser-level redirect loops can end up as a network error without a response.
     * We keep this fallback limited to mutating requests to avoid treating every
     * temporary network issue as a session timeout.
     */
    const method = error.config?.method?.toLowerCase();
    const isMutatingRequest = ['post', 'put', 'patch', 'delete'].includes(method);

    if (!error.response && error.code === 'ERR_NETWORK' && isMutatingRequest) {
        return true;
    }

    return false;
}

/**
 * Create one normalized payload for the frontend.
 */
function getSessionExpiredPayload(error) {
    return {
        message: error.response?.data?.message
            || t('users.session.expired'),
        reason: error.response?.data?.reason || 'session_expired',
        status: error.response?.status || null,
        originalError: error,
    };
}

/**
 * Emit a global browser event.
 *
 * Stores, layouts or pages can listen for this event and decide whether to show
 * a snackbar, modal or reload button.
 */
function dispatchSessionExpiredEvent(error) {
    if (sessionExpiredEventDispatched) {
        return;
    }

    sessionExpiredEventDispatched = true;

    window.dispatchEvent(new CustomEvent('editor-session-expired', {
        detail: getSessionExpiredPayload(error),
    }));
}

/**
 * Register an Axios HTTP interceptor to add the X-Socket-ID header.
 * @see: https://stackoverflow.com/a/59491510/5466203
 */
client.interceptors.request.use((config) => {
    // the function socketId () returns the id of the socket connection
    const socketId = echoInstance?.socketId?.();

    if (socketId) {
        config.headers['X-Socket-ID'] = socketId;
    }

    return config;
});

// @see: https://github.com/garethredfern/laravel-vue/blob/main/src/services/API.js
// client.interceptors.response.use(response => response, error => {
//     console.log(error);
//     return Promise.reject(error);
// });

/**
 * Register a response interceptor to normalize expired session/auth errors.
 */
client.interceptors.response.use(
    response => response,
    error => {
        if (isSessionExpiredError(error)) {
            dispatchSessionExpiredEvent(error);
        }

        return Promise.reject(error);
    }
);
